import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import type { MatchWithPlayersAndClub, Player } from "../../../shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Clock, X, FileEdit, Hourglass, Trophy, Shield } from "lucide-react";
import { TVStyleScoreboard } from "./tv-style-scoreboard";


interface LiveMatchCardProps {
  match: MatchWithPlayersAndClub;
  currentPlayerId?: string;
}

// Custom hook for countdown timer
function useCountdownTimer(endTime: Date | null) {
  const [timeRemaining, setTimeRemaining] = useState<string>("");

  useEffect(() => {
    if (!endTime) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiration = new Date(endTime).getTime() + (12 * 60 * 60 * 1000); // 12 hours after end time
      const difference = expiration - now;

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

        // Format: 00h00m with leading zeros
        setTimeRemaining(`${hours.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}m`);
      } else {
        setTimeRemaining("00h00m");
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  return timeRemaining;
}

import { useTranslation } from "react-i18next";

export default function LiveMatchCard({ match, currentPlayerId }: LiveMatchCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showThirdSetDialog, setShowThirdSetDialog] = useState(false);

  // Get countdown timer for awaiting confirmation
  const timeRemaining = useCountdownTimer(match.endTime);

  const scorePointMutation = useMutation({
    mutationFn: async ({ team, type }: { team: "A" | "B", type: "point" | "game" }) => {
      const response = await apiRequest("POST", `/api/matches/${match.id}/score`, { team, type });
      return response.json();
    },
    onSuccess: (data) => {
      // Check if server requires third set choice
      if (data.requiresThirdSetChoice && canSeeThirdSetDialog()) {
        setShowThirdSetDialog(true);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/matches/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches", match.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches"] });
      queryClient.invalidateQueries({ queryKey: ["/api/players/rankings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/players"] });
    },
    onError: (error) => {
      toast({
        title: t('matches.card.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const confirmMatchMutation = useMutation({
    mutationFn: async (playerId: string) => {
      const response = await apiRequest("POST", `/api/matches/${match.id}/confirm`, { playerId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/matches/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches", match.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches"] });
      toast({
        title: t('matches.card.matchConfirmed'),
        description: t('matches.card.matchConfirmedDesc'),
      });
    },
    onError: (error) => {
      toast({
        title: t('matches.card.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const cancelMatchMutation = useMutation({
    mutationFn: async (playerId: string) => {
      const response = await apiRequest("DELETE", `/api/matches/${match.id}/cancel`, { playerId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/matches/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches", match.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches"] });
      toast({
        title: t('matches.card.matchCancelled'),
        description: t('matches.card.matchCancelledDesc'),
      });
    },
    onError: (error) => {
      toast({
        title: t('matches.card.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const revertScoreMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/matches/${match.id}/revert`, {});
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/matches/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches", match.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches"] });
      queryClient.invalidateQueries({ queryKey: ["/api/players/rankings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/players"] });
      toast({
        title: t('matches.card.reverted'),
        description: t('matches.card.revertedDesc'),
      });
    },
    onError: (error) => {
      toast({
        title: t('matches.card.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Check if current user can see the third set choice dialog
  const canSeeThirdSetDialog = () => {
    if (!currentPlayerId) return false;
    const isPlayer =
      match.teamAPlayer1Id === currentPlayerId ||
      match.teamAPlayer2Id === currentPlayerId ||
      match.teamBPlayer1Id === currentPlayerId ||
      match.teamBPlayer2Id === currentPlayerId;
    const isCreator = match.createdByPlayerId === currentPlayerId;
    return isPlayer || isCreator;
  };

  const thirdSetChoiceMutation = useMutation({
    mutationFn: async (isSuperTiebreak: boolean) => {
      const response = await apiRequest("POST", `/api/matches/${match.id}/third-set-choice`, { isSuperTiebreak });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/matches/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches", match.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches"] });
      setShowThirdSetDialog(false);
    },
    onError: (error: any) => {
      toast({
        title: t('matches.card.error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const getCurrentSetScores = () => {
    const setIndex = match.currentSet - 1;
    const teamAScore = match.teamAScore[setIndex] || 0;
    const teamBScore = match.teamBScore[setIndex] || 0;
    return { teamAScore, teamBScore };
  };

  const getDuration = () => {
    // For completed matches, use stored duration or calculate from startTime to endTime
    if (match.isCompleted && match.duration) {
      const duration = Math.max(match.duration, 60);
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      const seconds = duration % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    // For active matches, calculate from startTime to now
    if (match.isCompleted && match.endTime) {
      const start = new Date(match.startTime);
      const end = new Date(match.endTime);
      const diffInSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);

      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.floor((diffInSeconds % 3600) / 60);
      const seconds = diffInSeconds % 60;

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    // Fallback for active matches
    const start = new Date(match.startTime);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - start.getTime()) / 1000);

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format duration as MM:SS for the bottom banner (matching history tab style)
  const getDurationFormatted = () => {
    // For completed matches, use stored duration
    if (match.isCompleted && match.duration) {
      const duration = Math.max(match.duration, 60);
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    // For completed matches with endTime, calculate from startTime to endTime
    if (match.isCompleted && match.endTime) {
      const start = new Date(match.startTime);
      const end = new Date(match.endTime);
      const diffInSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
      const minutes = Math.floor(diffInSeconds / 60);
      const seconds = diffInSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    // For active matches, calculate from startTime to now
    const start = new Date(match.startTime);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - start.getTime()) / 1000);
    const minutes = Math.floor(diffInSeconds / 60);
    const seconds = diffInSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const { teamAScore, teamBScore } = getCurrentSetScores();

  const getTimeUntilAutoConfirm = (endTime: string | Date) => {
    const end = typeof endTime === 'string' ? new Date(endTime) : endTime;
    const now = new Date();
    const timeLeft = (end.getTime() + (24 * 60 * 60 * 1000)) - now.getTime();

    if (timeLeft <= 0) return "soon";

    const hours = Math.floor(timeLeft / (60 * 60 * 1000));
    const minutes = Math.floor((timeLeft % (60 * 60 * 1000)) / (60 * 1000));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Check if current player is club admin (needed for both completed and active matches)
  const { data: clubMembers } = useQuery({
    queryKey: [`/api/clubs/${match.clubId}/members`],
    enabled: !!match.clubId && !!currentPlayerId,
  });
  const isClubAdmin = clubMembers?.find((m: any) => m.id === currentPlayerId)?.isAdmin || false;
  const isMatchCreator = currentPlayerId && match.createdByPlayerId === currentPlayerId;

  // Fetch creator player data if creator is not in the match
  const creatorInMatch = match.createdByPlayerId && (
    match.teamAPlayer1?.id === match.createdByPlayerId ||
    match.teamAPlayer2?.id === match.createdByPlayerId ||
    match.teamBPlayer1?.id === match.createdByPlayerId ||
    match.teamBPlayer2?.id === match.createdByPlayerId
  );

  const { data: creatorPlayer } = useQuery<Player>({
    queryKey: ["/api/players", match.createdByPlayerId],
    queryFn: async () => {
      if (!match.createdByPlayerId) return null;
      const response = await apiRequest("GET", `/api/players/${match.createdByPlayerId}`);
      return response.json();
    },
    enabled: !!match.createdByPlayerId && !creatorInMatch,
  });

  if (match.isCompleted) {
    const teamAPlayers = [match.teamAPlayer1.id, match.teamAPlayer2.id];
    const teamBPlayers = [match.teamBPlayer1.id, match.teamBPlayer2.id];

    const isTeamAPlayer = currentPlayerId && teamAPlayers.includes(currentPlayerId);
    const isTeamBPlayer = currentPlayerId && teamBPlayers.includes(currentPlayerId);
    const isMatchPlayer = isTeamAPlayer || isTeamBPlayer;

    // Build the set of all confirmed player IDs
    const allConfirmedIds = new Set([
      match.teamAConfirmedBy,
      (match as any).teamAPlayer2ConfirmedBy,
      match.teamBConfirmedBy,
      (match as any).teamBPlayer2ConfirmedBy,
    ].filter(Boolean));

    const requiredConfirmers: string[] = (match as any).requiredConfirmers || [];

    const teamARequired = requiredConfirmers.filter(id =>
      id === match.teamAPlayer1.id || id === match.teamAPlayer2.id
    );
    const teamBRequired = requiredConfirmers.filter(id =>
      id === match.teamBPlayer1.id || id === match.teamBPlayer2.id
    );

    // Team is fully confirmed when ≥1 member confirmed AND all required members confirmed
    const teamAConfirmed = !!match.teamAConfirmedBy && teamARequired.every(id => allConfirmedIds.has(id));
    const teamBConfirmed = !!match.teamBConfirmedBy && teamBRequired.every(id => allConfirmedIds.has(id));

    // Current player has already confirmed if their ID is in allConfirmedIds
    const currentPlayerAlreadyConfirmed = !!currentPlayerId && allConfirmedIds.has(currentPlayerId);

    // Admin can confirm for any team
    // Players can only confirm for their team (and only if they haven't already confirmed)
    // Creator can confirm for either team ONLY if they are not playing (organizer mode)
    const canConfirmTeamA = (isTeamAPlayer || isClubAdmin || (isMatchCreator && !isMatchPlayer)) && !teamAConfirmed && !currentPlayerAlreadyConfirmed && currentPlayerId;
    const canConfirmTeamB = (isTeamBPlayer || isClubAdmin || (isMatchCreator && !isMatchPlayer)) && !teamBConfirmed && !currentPlayerAlreadyConfirmed && currentPlayerId;
    const canConfirm = canConfirmTeamA || canConfirmTeamB;

    // Extract last names only (same as history tab)
    const getFirstName = (fullName: string) => {
      const parts = fullName.split(' ');
      return parts.length > 1 ? parts[parts.length - 1] : parts[0];
    };

    // Admin Only Validation Logic
    const isAdminOnlyValidation = match.club?.validationMethod === 'admin_only';

    // Helper to render the appropriate button
    const renderConfirmationButton = (team: 'A' | 'B', isConfirmed: boolean, canConfirm: boolean, alreadyConfirmedByMe: boolean) => {
      // 1. Already Confirmed (team fully confirmed)
      if (isConfirmed) {
        return (
          <button
            className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-md text-xs font-medium"
            disabled
            data-testid={`button-team-${team.toLowerCase()}-confirmed`}
          >
            <Check className="w-3 h-3" />
            <span>{t('matches.card.confirmed')}</span>
          </button>
        );
      }

      // 2. Admin Only Validation (and user is NOT admin/authorized effectively if we are here)
      // If validation is admin only, and the user is NOT an admin, we show the special disabled button
      // We check this BEFORE the generic 'canConfirm' because a player might technically 'canConfirm' based on participation
      // but 'admin_only' overrides that right.
      if (isAdminOnlyValidation && !isClubAdmin) {
        return (
          <button
            className="flex items-center gap-1 px-2 py-1 bg-muted text-muted-foreground rounded-md text-xs font-medium border border-border cursor-not-allowed"
            disabled
            title={t('matches.card.onlyAdminsCanConfirm')}
            data-testid={`button-team-${team.toLowerCase()}-admin-only`}
          >
            <Shield className="w-3 h-3" />
            <span>{t('matches.card.confirmAdminOnly')}</span>
          </button>
        );
      }

      // 3. Can Confirm (Admin or Authorized Player)
      if (canConfirm) {
        return (
          <button
            onClick={() => confirmMatchMutation.mutate(currentPlayerId)}
            disabled={confirmMatchMutation.isPending}
            className="flex items-center gap-1 px-3 py-1.5 bg-yellow-400 text-yellow-900 hover:bg-yellow-500 transition-all duration-1000 rounded-lg text-xs font-bold shadow-md border-2 border-yellow-600"
            style={{ animation: 'pulse 3s infinite' }}
            data-testid={`button-team-${team.toLowerCase()}-confirm`}
            title={isClubAdmin ? `As club admin, you can confirm for Team ${team}` : ""}
          >
            <Clock className="w-3 h-3" />
            <span>{confirmMatchMutation.isPending ? t('matches.card.confirming') : (isClubAdmin ? t('matches.card.confirmAdmin') : t('matches.card.confirmScore'))}</span>
          </button>
        );
      }

      // 4. Already confirmed by me, awaiting partner
      if (alreadyConfirmedByMe) {
        return (
          <button
            className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs font-medium opacity-80"
            disabled
            data-testid={`button-team-${team.toLowerCase()}-awaiting-partner`}
          >
            <Check className="w-3 h-3" />
            <span>{t('matches.card.awaitingPartner')}</span>
          </button>
        );
      }

      // 5. Default Awaiting (For teammate waiting for other, or opponent waiting)
      return (
        <button
          className="flex items-center gap-1 px-2 py-1 bg-yellow-50 text-yellow-600 rounded-md text-xs font-medium opacity-60"
          disabled
          data-testid={`button-team-${team.toLowerCase()}-awaiting`}
        >
          <Clock className="w-3 h-3" />
          <span>{t('matches.card.awaitingBtn')}</span>
        </button>
      );
    };

    // Get final scores for display
    const getFinalSetScore = (setIndex: number, team: "A" | "B") => {
      const scores = team === "A" ? match.teamAScore : match.teamBScore;
      return scores[setIndex] || 0;
    };

    return (
      <Card className="bg-card border border-border overflow-hidden" data-testid="completed-match-card">
        {/* Clean Header for Confirmation */}
        <div className="bg-yellow-50 px-4 py-2 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {match.isConfirmed ? (
                <>
                  <Check className="w-3 h-3 text-green-600" />
                  <span className="text-xs font-medium text-green-700">{t('matches.card.confirmed')}</span>
                </>
              ) : (
                <>
                  <Clock className="w-3 h-3 text-yellow-600" />
                  <span className="text-xs font-medium text-yellow-700">{t('matches.card.awaiting')}</span>
                  {timeRemaining && (
                    <span className="flex items-center gap-1 text-xs text-red-600 font-medium ml-2">
                      <Hourglass className="w-3 h-3 text-red-600" />
                      {timeRemaining}
                    </span>
                  )}
                </>
              )}
              {currentPlayerId && (
                [match.teamAPlayer1.id, match.teamAPlayer2.id, match.teamBPlayer1.id, match.teamBPlayer2.id].includes(currentPlayerId) ||
                isMatchCreator || isClubAdmin
              ) && !match.isConfirmed && (
                  <button
                    onClick={() => currentPlayerId && cancelMatchMutation.mutate(currentPlayerId)}
                    className="ml-2 text-red-500 hover:text-red-700 transition-colors p-1"
                    data-testid="button-cancel-match"
                    title={t('matches.card.cancel')}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
            </div>
            <span className="text-xs text-muted-foreground">{t('matches.card.completed')}</span>
          </div>
        </div>

        {/* Minimal Scoreboard for Final Score */}
        <div className="p-4">
          {/* Team A */}
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="font-medium text-foreground">
                {getFirstName(match.teamAPlayer1.name)} / {getFirstName(match.teamAPlayer2.name)}
              </div>
              {!match.isConfirmed && renderConfirmationButton('A', teamAConfirmed, !!canConfirmTeamA, !!currentPlayerId && isTeamAPlayer && allConfirmedIds.has(currentPlayerId) && !teamAConfirmed)}
            </div>
            <div className="flex items-center gap-4">
              {/* Show all set scores */}
              {match.teamAScore.map((score, index) => (
                <div key={index} className="text-sm text-muted-foreground min-w-[24px] text-center">
                  {score}
                </div>
              ))}
            </div>
          </div>

          {/* Team B */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="font-medium text-foreground">
                {getFirstName(match.teamBPlayer1.name)} / {getFirstName(match.teamBPlayer2.name)}
              </div>
              {!match.isConfirmed && renderConfirmationButton('B', teamBConfirmed, !!canConfirmTeamB, !!currentPlayerId && isTeamBPlayer && allConfirmedIds.has(currentPlayerId) && !teamBConfirmed)}
            </div>
            <div className="flex items-center gap-4">
              {/* Show all set scores */}
              {match.teamBScore.map((score, index) => (
                <div key={index} className="text-sm text-muted-foreground min-w-[24px] text-center">
                  {score}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Duration and Club Info - Same yellow background as header when awaiting confirmation */}
        {!match.isConfirmed && (
          <div className="bg-yellow-50 px-4 py-2 border-t">
            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <div className="flex items-center gap-3">
                {/* Duration */}
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  {getDurationFormatted()}
                </span>
                {/* Creator */}
                {match.createdByPlayerId && (() => {
                  // First try to find creator in match players
                  const creatorInMatch =
                    match.teamAPlayer1?.id === match.createdByPlayerId ? match.teamAPlayer1 :
                      match.teamAPlayer2?.id === match.createdByPlayerId ? match.teamAPlayer2 :
                        match.teamBPlayer1?.id === match.createdByPlayerId ? match.teamBPlayer1 :
                          match.teamBPlayer2?.id === match.createdByPlayerId ? match.teamBPlayer2 :
                            null;

                  // If not in match, use fetched creator player
                  const creator = creatorInMatch || creatorPlayer;
                  const creatorName = creator ? getFirstName(creator.name) : null;

                  return creatorName ? (
                    <span className="flex items-center gap-1">
                      <FileEdit className="w-3 h-3 text-muted-foreground" />
                      {creatorName}
                    </span>
                  ) : null;
                })()}
              </div>
              {/* Club */}
              {match.club && (
                <span className="flex items-center gap-1">
                  <img src="/tennis-ball.png" alt="Club" className="w-3 h-3" />
                  {match.club.name}
                </span>
              )}
            </div>
          </div>
        )}

      </Card>
    );
  }

  // For active matches, use the TV-style scoreboard
  return (
    <>
      <TVStyleScoreboard
        match={match}
        onAddPoint={(team) => scorePointMutation.mutate({ team, type: "point" })}
        onAddGame={(team) => scorePointMutation.mutate({ team, type: "game" })}
        onViewMatch={() => { }}
        onCancelMatch={() => currentPlayerId && cancelMatchMutation.mutate(currentPlayerId)}
        onRevert={() => revertScoreMutation.mutate()}
        isLoading={scorePointMutation.isPending || cancelMatchMutation.isPending || revertScoreMutation.isPending}
        playerId={currentPlayerId}
      />

      {/* Third Set Choice Dialog */}
      <Dialog open={showThirdSetDialog} onOpenChange={() => { }}>
        <DialogContent className="sm:max-w-md [&>button]:hidden">
          <DialogHeader>
            <div className="flex items-center space-x-2 mb-2">
              <Trophy className="w-5 h-5 text-padel-green" />
              <DialogTitle className="text-lg font-semibold text-foreground">
                {t('matches.card.thirdSet.title')}
              </DialogTitle>
            </div>
            <DialogDescription className="text-muted-foreground">
              {t('matches.card.thirdSet.desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              onClick={() => thirdSetChoiceMutation.mutate(false)}
              disabled={thirdSetChoiceMutation.isPending}
              className="w-full h-12 text-base font-medium border-2 border-border hover:bg-muted hover:border-border transition-colors"
              variant="outline"
            >
              {t('matches.card.thirdSet.regular')}
            </Button>
            <Button
              onClick={() => thirdSetChoiceMutation.mutate(true)}
              disabled={thirdSetChoiceMutation.isPending}
              className="w-full h-12 text-base font-medium bg-padel-green hover:bg-padel-green-light text-white border-2 border-padel-green transition-colors shadow-sm"
            >
              {t('matches.card.thirdSet.superTiebreak')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
